import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Form, Input, Button, message, Typography, Space } from "antd";
import {
  KeyOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  CheckCircleOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";

const { Title, Text } = Typography;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await apiClient.post(`/password-reset/reset-password/${token}`, {
        newPassword: values.password,
      });

      message.success("Password reset successfully!");
      setResetSuccess(true);

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Password reset error:", error);
      message.error(
        error.response?.data?.message || "Invalid or expired reset link",
      );
    } finally {
      setLoading(false);
    }
  };

  if (resetSuccess) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Card style={{ width: 400, textAlign: "center" }}>
          <Space direction="vertical" size="large">
            <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a" }} />
            <Title level={3}>Password Reset Successful!</Title>
            <Text>You can now log in with your new password.</Text>
            <Button type="primary" onClick={() => navigate("/login")}>
              Go to Login
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      <Card style={{ width: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <KeyOutlined
            style={{ fontSize: 48, color: "#1890ff", marginBottom: 16 }}
          />
          <Title level={3}>Set Your Password</Title>
          <Text type="secondary">Enter your new password below</Text>
        </div>

        <Form
          form={form}
          name="resetPassword"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: "Please enter your password!" },
              { min: 6, message: "Minimum 6 characters required" },
            ]}
          >
            <Input.Password
              placeholder="Enter password"
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm password!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{ height: 48 }}
            >
              Set Password
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ResetPassword;
